import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Skeleton } from "./Skeleton";

interface TotalPointsChartProps {
  from?: string;
  to?: string;
}

export function TotalPointsChart({ from, to }: TotalPointsChartProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["total-points-history", from, to],
    queryFn: async () => {
      const { data, error } = await api.points["total-history"].get({
        query: { from, to },
      });
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return <Skeleton kind="chart" />;
  }

  if (error || !data?.data || data.data.length < 2) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        Not enough snapshots to draw a chart yet.
      </div>
    );
  }

  const chartData = data.data.map((point) => ({
    date: new Date(point.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    total: point.totalPoints,
    primary: point.primaryPoints,
    secondary: point.secondaryPoints,
    bonus: point.bonusPoints,
  }));

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2739" />
          <XAxis
            dataKey="date"
            stroke="#5b6982"
            tick={{ fill: "#8b98b0", fontSize: 12 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="#5b6982"
            tick={{ fill: "#8b98b0", fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) =>
              value >= 1000000
                ? `${(value / 1000000).toFixed(1)}M`
                : value >= 1000
                ? `${(value / 1000).toFixed(0)}k`
                : value
            }
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#131826",
              border: "1px solid #2c374d",
              borderRadius: 10,
            }}
            labelStyle={{ color: "#8b98b0" }}
            itemStyle={{ color: "#eaeef6" }}
            formatter={(value) => [
              typeof value === "number" ? value.toLocaleString() : String(value),
              "",
            ]}
          />
          <Legend wrapperStyle={{ color: "#8b98b0", fontSize: 12 }} />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#f0b84a"
            strokeWidth={2}
            dot={false}
            name="Total"
          />
          <Line
            type="monotone"
            dataKey="primary"
            stroke="#a394ff"
            strokeWidth={2}
            dot={false}
            name="Primary"
          />
          <Line
            type="monotone"
            dataKey="secondary"
            stroke="#5cd4f0"
            strokeWidth={2}
            dot={false}
            name="Secondary"
          />
          <Line
            type="monotone"
            dataKey="bonus"
            stroke="#8b98b0"
            strokeWidth={2}
            dot={false}
            name="Bonus"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
